import { useMemo, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

import {
  useCreateUserStandardMutation,
  useGetAllUserStandardsQuery,
} from '../../standardsCatalogApi';
import { buildUserStandardsTree, type UserStandardTreeNode } from '../../utils/userStandardsTree';

import type { UUID } from '@/shared/types/userStandard';

interface DestinationPickerProps {
  ownerId: UUID;
  value: UUID | null;
  onChange: (id: UUID | null) => void;
  /** Hide this node and its subtree (e.g. so a node can't be moved into itself). */
  excludeId?: UUID;
  allowTopLevel?: boolean;
}

function DestinationRow({
  node,
  depth,
  value,
  expanded,
  onChange,
  onToggleExpand,
}: {
  node: UserStandardTreeNode;
  depth: number;
  value: UUID | null;
  expanded: Set<string>;
  onChange: (id: UUID) => void;
  onToggleExpand: (id: string) => void;
}) {
  // Any node can be a destination now, so every child is shown (not just folders).
  const childNodes = node.children;
  const isOpen = expanded.has(node.id);
  const selected = value === node.id;
  const isFolder = node.node_type === 'folder';

  return (
    <>
      <Box
        onClick={() => onChange(node.id)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: `${8 + depth * 18}px`,
          pr: 1,
          py: 0.5,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: selected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
        }}
      >
        <IconButton
          size="small"
          sx={{ visibility: childNodes.length ? 'visible' : 'hidden', p: 0.25 }}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand(node.id);
          }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <ChevronRightIcon
            fontSize="small"
            sx={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
          />
        </IconButton>
        {isFolder ? (
          <FolderOutlinedIcon fontSize="small" color={selected ? 'primary' : 'action'} />
        ) : (
          <DescriptionOutlinedIcon fontSize="small" color={selected ? 'primary' : 'action'} />
        )}
        <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }} noWrap>
          {node.code || node.title}
        </Typography>
      </Box>
      {isOpen &&
        childNodes.map((child) => (
          <DestinationRow
            key={child.id}
            node={child}
            depth={depth + 1}
            value={value}
            expanded={expanded}
            onChange={onChange}
            onToggleExpand={onToggleExpand}
          />
        ))}
    </>
  );
}

export function DestinationPicker({
  ownerId,
  value,
  onChange,
  excludeId,
  allowTopLevel = false,
}: DestinationPickerProps) {
  const { data: rows = [] } = useGetAllUserStandardsQuery({ ownerId });
  const [createNode, { isLoading: creating }] = useCreateUserStandardMutation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [newFolderName, setNewFolderName] = useState('');

  const destinationTree = useMemo(() => {
    const tree = buildUserStandardsTree(rows);
    // Every node is a valid destination; only the moved node's own subtree is pruned so it can't be
    // dropped into itself.
    const prune = (nodes: UserStandardTreeNode[]): UserStandardTreeNode[] =>
      nodes.filter((n) => n.id !== excludeId).map((n) => ({ ...n, children: prune(n.children) }));
    return prune(tree);
  }, [rows, excludeId]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleCreateFolder = async () => {
    const title = newFolderName.trim();
    if (!title) return;
    const created = await createNode({
      ownerId,
      title,
      nodeType: 'folder',
      parentId: value,
    }).unwrap();
    setNewFolderName('');
    if (value) setExpanded((prev) => new Set(prev).add(value));
    onChange(created.id);
  };

  return (
    <Box>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          maxHeight: 240,
          overflow: 'auto',
          py: 0.5,
        }}
      >
        {allowTopLevel && (
          <Box
            onClick={() => onChange(null)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: '8px',
              pr: 1,
              py: 0.5,
              cursor: 'pointer',
              borderRadius: 1,
              bgcolor: value === null ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: value === null ? 'action.selected' : 'action.hover' },
            }}
          >
            {/* Hidden chevron matching the folder rows' expand slot so labels align. */}
            <IconButton
              size="small"
              sx={{ visibility: 'hidden', p: 0.25 }}
              aria-hidden
              tabIndex={-1}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <LibraryBooksIcon fontSize="small" color={value === null ? 'primary' : 'action'} />
            <Typography variant="body2" sx={{ fontWeight: value === null ? 600 : 400 }} noWrap>
              Top level (its own collection)
            </Typography>
          </Box>
        )}
        {destinationTree.length === 0 ? (
          allowTopLevel ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 1 }}>
              <LibraryBooksIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                No collections yet — create one below.
              </Typography>
            </Box>
          )
        ) : (
          destinationTree.map((node) => (
            <DestinationRow
              key={node.id}
              node={node}
              depth={0}
              value={value}
              expanded={expanded}
              onChange={onChange}
              onToggleExpand={toggleExpand}
            />
          ))
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          value={newFolderName}
          placeholder={value ? 'New subfolder name' : 'New collection name'}
          onChange={(event) => setNewFolderName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleCreateFolder();
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<CreateNewFolderOutlinedIcon />}
          disabled={!newFolderName.trim() || creating}
          onClick={handleCreateFolder}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}

export default DestinationPicker;
